import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import Icon from '@mdi/react';
import { mdiBaseballDiamondOutline } from '@mdi/js';
import { FaClipboardList, FaPeopleGroup, FaFutbol } from 'react-icons/fa6';
import useMyDetails from '../../hooks/useMyDetails';
import { useActiveLeague } from '../../App';

function ProfileSidebarLeft() {
  const { activeLeague } = useActiveLeague();
  const {
    data: userData,
    isLoading: detailsLoading,
    error: detailsError,
  } = useMyDetails();

  const leaguePlayer = useMemo(() => {
    const players = userData?.players || [];
    if (!activeLeague?.id) return null;
    return (
      players.find((player) => Number(player.league_id) === Number(activeLeague.id)) ||
      null
    );
  }, [userData?.players, activeLeague?.id]);

  const leagueTeam = leaguePlayer?.team || null;
  const gamesPlayed = leaguePlayer?.games_played ?? 0;
  const gamesWon = leaguePlayer?.games_won ?? 0;
  const gamesLost = leaguePlayer?.games_lost ?? 0;

  // Handle backdrop cleanup when offcanvas is hidden
  useEffect(() => {
    const handleOffcanvasHidden = () => {
      // Simple cleanup - just remove any backdrop that exists
      const backdrop = document.querySelector('.offcanvas-backdrop');
      if (backdrop) {
        backdrop.remove();
      }
      document.body.classList.remove('modal-open');
    };

    // Get the specific offcanvas element
    const offcanvasElement = document.getElementById('offcanvasSideNavbar');

    if (offcanvasElement) {
      // Listen for the hidden event on this specific offcanvas
      offcanvasElement.addEventListener('hidden.bs.offcanvas', handleOffcanvasHidden);

      // Cleanup function
      return () => {
        offcanvasElement.removeEventListener('hidden.bs.offcanvas', handleOffcanvasHidden);
      };
    }
  }, []);

  if (detailsLoading) return <Spinner animation="border" size="sm" />;
  if (detailsError) return null;

  return (
    <>
      {/* Navbar START */}
      <nav className="navbar navbar-expand-lg mx-0">
        <div className="offcanvas offcanvas-start" tabIndex="-1" id="offcanvasSideNavbar" data-bs-backdrop="true">
          {/* Offcanvas header */}
          <div className="offcanvas-header">
            <button
              type="button"
              className="btn-close text-reset ms-auto"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            ></button>
          </div>

          {/* Offcanvas body */}
          <div className="offcanvas-body d-block px-2 px-lg-0">
            {/* Card START */}
            <div className="card overflow-hidden">
              {/* Cover image */}
              <div
                className="h-50px"
                style={
                  leagueTeam?.color
                    ? { backgroundColor: leagueTeam.color }
                    : {
                      backgroundImage: `url(/static/images/kb_static/lemont_rec_league_banner.png)`,
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                    }
                }
              />

              {/* Card body START */}
              <div className="card-body pt-0">
                <div className="text-center">
                  {/* Avatar */}
                  <div className="avatar avatar-lg mt-n5 mb-3">
                    <a href="#!">
                      <img
                        className="avatar-img rounded border border-white border-3"
                        src={
                          userData.profile_picture ? userData.profile_picture : `/static/images/kb_static/lemont_rec_leagues_circle.png`
                        }
                        alt={`${userData.first_name} ${userData.last_name}`}
                      />
                    </a>
                  </div>
                  {/* Info */}
                  <h5 className="mb-0">
                    <a href="#!">{userData.first_name} {userData.last_name}</a>
                  </h5>
                  <small style={{ color: leagueTeam?.color || 'inherit' }}>
                    {leagueTeam?.name || 'No team'}
                  </small>
                  <p className="mt-3">{userData.bio}</p>

                  {/* User stat START — league-scoped lifetime from the selected league tab */}
                  <div className="hstack gap-2 gap-xl-3 justify-content-center">
                    <div>
                      <h6 className="mb-0">{gamesPlayed}</h6>
                      <small>Games Played</small>
                    </div>
                    <div className="vr"></div>
                    <div>
                      <h6 className="mb-0">{gamesWon}</h6>
                      <small>Wins</small>
                    </div>
                    <div className="vr"></div>
                    <div>
                      <h6 className="mb-0">{gamesLost}</h6>
                      <small>Loses</small>
                    </div>
                  </div>
                  {/* User stat END */}
                </div>

                {/* Divider */}
                <hr />

                {/* Side Nav START */}
                <ul className="nav nav-link-secondary flex-column fw-bold gap-2">
                  <li className="nav-item">
                    <Link className="nav-link" to="/profile">
                      <FaClipboardList className="me-2" size={16} />
                      <span>Feed</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link" to="/teams/user-teams">
                      <FaPeopleGroup className="me-2" size={16} />
                      <span>My Teams</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link" to="/leagues/seasons/select">
                      <FaFutbol className="me-2" size={16} />
                      <span>Seasons</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link" to="/games/current-season">
                      <Icon path={mdiBaseballDiamondOutline} size={1} />
                      <span> Games</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link" to="/notifications/list">
                      <i className="bi bi-bell fa-fw me-2" size={16} style={{ fontWeight: 'bold' }}></i>
                      <span>Notifications</span>
                    </Link>
                  </li>
                </ul>
                {/* Side Nav END */}
              </div>
              {/* Card body END */}
              {/* Card footer */}
              <div className="card-footer text-center py-2">
                <a className="btn btn-link btn-sm" href="/profile/">
                  View Profile
                </a>
              </div>
            </div>
            {/* Card END */}

            <p className="small text-center mt-1">
              ©{new Date().getFullYear()}{' '}
              <a className="text-reset" target="_blank" href="/" rel="noreferrer">
                Lemont Rec Leagues
              </a>
            </p>
          </div>
        </div>
      </nav>
      {/* Navbar END */}
    </>
  );
}

export default ProfileSidebarLeft;
